/**
 * SupervisorPanel — Right sidebar panel for workflow-level AI Supervisor config
 *
 * Shows when no node is selected (or when "Supervisor" tab is clicked).
 * Edits: purpose, success criteria, constraints, supervisor mode.
 * Also shows validation history during test runs.
 */

import { useState } from 'react';
import {
  Brain, Shield, CheckCircle, AlertTriangle, Plus, X,
  ChevronDown, Sparkles, Target, Eye, Bot,
} from 'lucide-react';
import { useSupervisorContext } from '@/lib/use-automate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  type SupervisorMode,
  type SuccessCriterion,
  type CriteriaType,
  type CriteriaEval,
  type CriteriaWeight,
} from '@/lib/mock-automate-data';

interface SupervisorPanelProps {
  purpose: string;
  onPurposeChange: (purpose: string) => void;
  criteria: SuccessCriterion[];
  onCriteriaChange: (criteria: SuccessCriterion[]) => void;
  constraints: string[];
  onConstraintsChange: (constraints: string[]) => void;
  supervisorMode: SupervisorMode;
  onSupervisorModeChange: (mode: SupervisorMode) => void;
  /** UUID of the live workflow — enables the auto-derived context preview. */
  workflowId?: string;
  // Test run validation results (shown during/after test)
  validationResults?: Array<{
    criterionId: string;
    result: 'pass' | 'partial' | 'fail';
    evidence: string;
  }>;
}

export function SupervisorPanel({
  purpose, onPurposeChange,
  criteria, onCriteriaChange,
  constraints, onConstraintsChange,
  supervisorMode, onSupervisorModeChange,
  workflowId,
  validationResults,
}: SupervisorPanelProps) {
  // Pull what the supervisor will know about THIS workflow (derived
  // server-side from purpose + nodes + criteria + constraints). Refreshes
  // automatically when the workflow definition changes.
  const { data: derived } = useSupervisorContext(workflowId);
  const [derivedOpen, setDerivedOpen] = useState(true);
  const [newCriterionDesc, setNewCriterionDesc] = useState('');
  const [newCriterionType, setNewCriterionType] = useState<CriteriaType>('completeness');
  const [newCriterionEval, setNewCriterionEval] = useState<CriteriaEval>('ai');
  const [newCriterionWeight, setNewCriterionWeight] = useState<CriteriaWeight>('critical');
  const [newConstraint, setNewConstraint] = useState('');
  const [purposeOpen, setPurposeOpen] = useState(true);
  const [criteriaOpen, setCriteriaOpen] = useState(true);
  const [constraintsOpen, setConstraintsOpen] = useState(true);

  const handleAddCriterion = () => {
    if (!newCriterionDesc.trim()) return;
    onCriteriaChange([...criteria, {
      id: `sc-${Date.now()}`,
      description: newCriterionDesc.trim(),
      type: newCriterionType,
      evaluation: newCriterionEval,
      weight: newCriterionWeight,
    }]);
    setNewCriterionDesc('');
  };

  const handleAddConstraint = () => {
    if (!newConstraint.trim()) return;
    onConstraintsChange([...constraints, newConstraint.trim()]);
    setNewConstraint('');
  };

  return (
    <div className="w-[320px] border-l bg-card flex flex-col shrink-0">
      {/* Header */}
      <div className="p-3 border-b flex items-center gap-2">
        <Brain className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold flex-1">AI Supervisor</span>
        <Badge variant="outline" className={cn('text-[9px]',
          supervisorMode === 'thorough' ? 'text-violet-500 dark:text-violet-400' :
          supervisorMode === 'standard' ? 'text-blue-500 dark:text-blue-400' : 'text-zinc-500'
        )}>
          {supervisorMode}
        </Badge>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">

          {/* Auto-derived supervisor context — shows what the AI
              supervisor LEARNED from this workflow's definition. The
              user only writes a one-line purpose + criteria; the
              supervisor expands that into the role + watchpoints +
              red lines you see here. Updates server-side whenever
              the workflow saves. */}
          {workflowId && derived && (
            <Collapsible open={derivedOpen} onOpenChange={setDerivedOpen}>
              <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left py-1">
                <ChevronDown className={cn('h-3 w-3 text-muted-foreground transition-transform', !derivedOpen && '-rotate-90')} />
                <Bot className="h-3 w-3 text-primary" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  What the supervisor learned
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-1.5 space-y-2">
                <div className="rounded-md border border-primary/20 bg-primary/5 px-2 py-2 space-y-1.5">
                  <p className="text-[10px] leading-relaxed text-foreground/90 italic">
                    {derived.derivedRole}
                  </p>
                </div>
                {derived.watchpoints.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Eye className="h-2.5 w-2.5" /> Watchpoints
                    </p>
                    {derived.watchpoints.map((w, i) => (
                      <div key={i} className="text-[10px] text-muted-foreground pl-1">
                        • {w}
                      </div>
                    ))}
                  </div>
                )}
                {derived.redLines.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Shield className="h-2.5 w-2.5 text-red-500" /> Red lines
                    </p>
                    {derived.redLines.map((r, i) => (
                      <div key={i} className="text-[10px] text-muted-foreground pl-1">
                        • {r}
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[9px] text-muted-foreground italic pt-0.5">
                  Auto-derived from this workflow's purpose, nodes, criteria, and constraints. Updates whenever you save.
                </p>
              </CollapsibleContent>
            </Collapsible>
          )}

          {workflowId && derived && <Separator />}

          {/* Supervisor Mode */}
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Supervision Level</Label>
            <RadioGroup value={supervisorMode} onValueChange={v => onSupervisorModeChange(v as SupervisorMode)} className="space-y-1">
              {[
                { value: 'minimal', label: 'Minimal', desc: 'Final output only', color: 'text-zinc-500' },
                { value: 'standard', label: 'Standard', desc: 'Each agent step', color: 'text-blue-500' },
                { value: 'thorough', label: 'Thorough', desc: 'Every step + cross-check', color: 'text-violet-500' },
              ].map(opt => (
                <Label key={opt.value} className={cn('flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors text-xs', supervisorMode === opt.value ? 'border-primary bg-primary/5' : 'hover:border-primary/30')}>
                  <RadioGroupItem value={opt.value} />
                  <Brain className={cn('h-3 w-3', opt.color)} />
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-[10px] text-muted-foreground ml-auto">{opt.desc}</span>
                </Label>
              ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Purpose */}
          <Collapsible open={purposeOpen} onOpenChange={setPurposeOpen}>
            <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left py-1">
              <ChevronDown className={cn('h-3 w-3 text-muted-foreground transition-transform', !purposeOpen && '-rotate-90')} />
              <Target className="h-3 w-3 text-primary" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Purpose</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Textarea
                value={purpose}
                onChange={e => onPurposeChange(e.target.value)}
                placeholder="What should this workflow achieve?"
                rows={3}
                className="text-xs mt-1.5"
              />
            </CollapsibleContent>
          </Collapsible>

          <Separator />

          {/* Success Criteria */}
          <Collapsible open={criteriaOpen} onOpenChange={setCriteriaOpen}>
            <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left py-1">
              <ChevronDown className={cn('h-3 w-3 text-muted-foreground transition-transform', !criteriaOpen && '-rotate-90')} />
              <CheckCircle className="h-3 w-3 text-emerald-500" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Quality Criteria ({criteria.length})</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1.5 space-y-1.5">
              {criteria.map(c => {
                const vr = validationResults?.find(v => v.criterionId === c.id);
                return (
                  <div key={c.id} className={cn('flex items-start gap-1.5 bg-muted/30 rounded px-2 py-1.5 group', vr?.result === 'pass' && 'bg-emerald-500/5 border border-emerald-500/10', vr?.result === 'fail' && 'bg-red-500/5 border border-red-500/10')}>
                    {vr ? (
                      <span className={cn('h-3 w-3 shrink-0 mt-0.5', vr.result === 'pass' ? 'text-emerald-500' : vr.result === 'fail' ? 'text-red-500' : 'text-amber-500')}>
                        {vr.result === 'pass' ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                      </span>
                    ) : (
                      <CheckCircle className="h-3 w-3 text-muted-foreground/30 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] leading-tight">{c.description}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Badge variant="outline" className="text-[7px] h-3 px-1">{c.type}</Badge>
                        <Badge variant="outline" className={cn('text-[7px] h-3 px-1', c.weight === 'critical' ? 'text-red-500' : c.weight === 'important' ? 'text-amber-500' : '')}>{c.weight}</Badge>
                      </div>
                      {vr?.evidence && <p className="text-[9px] text-muted-foreground mt-0.5 italic">{vr.evidence}</p>}
                    </div>
                    <Button variant="ghost" size="icon" className="h-4 w-4 opacity-0 group-hover:opacity-100 text-destructive shrink-0" onClick={() => onCriteriaChange(criteria.filter(x => x.id !== c.id))} aria-label="Remove criterion" title="Remove criterion">
                      <X className="h-2.5 w-2.5" />
                    </Button>
                  </div>
                );
              })}

              {/* Add criterion inline */}
              <div className="space-y-1">
                <Input value={newCriterionDesc} onChange={e => setNewCriterionDesc(e.target.value)} placeholder="Add quality criterion..." className="h-7 text-[10px]" onKeyDown={e => { if (e.key === 'Enter') handleAddCriterion(); }} />
                <div className="flex items-center gap-1">
                  <Select value={newCriterionType} onValueChange={v => setNewCriterionType(v as CriteriaType)}>
                    <SelectTrigger className="h-6 text-[9px] flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completeness">Complete</SelectItem>
                      <SelectItem value="accuracy">Accurate</SelectItem>
                      <SelectItem value="format">Format</SelectItem>
                      <SelectItem value="safety">Safety</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={newCriterionWeight} onValueChange={v => setNewCriterionWeight(v as CriteriaWeight)}>
                    <SelectTrigger className="h-6 text-[9px] flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="important">Important</SelectItem>
                      <SelectItem value="nice_to_have">Nice</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="icon" className="h-6 w-6 shrink-0" onClick={handleAddCriterion} disabled={!newCriterionDesc.trim()} aria-label="Add criterion" title="Add criterion">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <Separator />

          {/* Constraints */}
          <Collapsible open={constraintsOpen} onOpenChange={setConstraintsOpen}>
            <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left py-1">
              <ChevronDown className={cn('h-3 w-3 text-muted-foreground transition-transform', !constraintsOpen && '-rotate-90')} />
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Constraints ({constraints.length})</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1.5 space-y-1">
              {constraints.map((c, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-amber-500/5 rounded px-2 py-1 group">
                  <AlertTriangle className="h-2.5 w-2.5 text-amber-500 shrink-0" />
                  <span className="text-[10px] flex-1">{c}</span>
                  <Button variant="ghost" size="icon" className="h-4 w-4 opacity-0 group-hover:opacity-100 text-destructive shrink-0" onClick={() => onConstraintsChange(constraints.filter((_, j) => j !== i))} aria-label="Remove constraint" title="Remove constraint">
                    <X className="h-2.5 w-2.5" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-1">
                <Input value={newConstraint} onChange={e => setNewConstraint(e.target.value)} placeholder="Add constraint..." className="h-7 text-[10px] flex-1" onKeyDown={e => { if (e.key === 'Enter') handleAddConstraint(); }} />
                <Button size="icon" className="h-7 w-7 shrink-0" onClick={handleAddConstraint} disabled={!newConstraint.trim()} aria-label="Add constraint" title="Add constraint">
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Validation Results Summary (shown after test run) */}
          {validationResults && validationResults.length > 0 && (
            <>
              <Separator />
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Eye className="h-3 w-3 text-primary" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Last Validation</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={cn('text-[9px]',
                    validationResults.every(v => v.result === 'pass') ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                    validationResults.some(v => v.result === 'fail') ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                    'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                  )}>
                    {validationResults.filter(v => v.result === 'pass').length}/{validationResults.length} passed
                  </Badge>
                </div>
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
